import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  TrendingUp,
  Activity,
  CreditCard,
  ArrowUpRight,
  } from "lucide-react";

interface Stats {
  totalTenants: number;
  activeTenants: number;
  inactiveTenants: number;
  availablePlans: number;
}

export default function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch("/api/super-admin/stats", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setStats(data.data);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-lyrax-border border-t-lyrax-primary rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Tenants",
      value: stats?.totalTenants ?? 0,
      icon: Building2,
      up: true,
      tone: "positive" as const,
    },
    {
      title: "Activos",
      value: stats?.activeTenants ?? 0,
      icon: TrendingUp,
      up: true,
      tone: "positive" as const,
    },
    {
      title: "Inactivos",
      value: stats?.inactiveTenants ?? 0,
      icon: Activity,
      up: false,
      tone: "negative" as const,
    },
    {
      title: "Planes",
      value: stats?.availablePlans ?? 0,
      icon: CreditCard,
      up: true,
      tone: "positive" as const,
    },
  ];

  return (
    <div>
      <div className="mb-2 text-sm text-lyrax-text-secondary">
        <span>Lyrax</span>
        <span className="mx-1.5 text-lyrax-border" aria-hidden>
          ›
        </span>
        <span>Admin</span>
        <span className="mx-1.5 text-lyrax-border" aria-hidden>
          ›
        </span>
        <span className="text-lyrax-text font-medium">General</span>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">Dashboard General</h1>
          <p className="page-subtitle">Resumen operativo de la plataforma Lyrax</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="btn-primary pointer-events-none text-xs">Dashboard General</span>
          <Link to="/tenants" className="btn-outline text-xs">
            Tenants
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {statCards.map(({ title, value, icon: Icon, up, tone }, i) => (
          <div
            key={title}
            className={`card group hover:-translate-y-0.5 transition-transform duration-300 animate-fade-up stagger-${i + 1}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <span
                  className={`h-2 w-2 rounded-full ${tone === "positive" ? "bg-lyrax-primary" : "bg-lyrax-error"
                    }`}
                />
                <p className="label-caps">{title}</p>
              </div>
              <div className="p-2 rounded-lyrax bg-lyrax-bg text-lyrax-text-secondary group-hover:text-lyrax-primary transition-colors">
                <Icon className="w-4 h-4" />
              </div>
            </div>
            <p className="text-3xl font-bold text-lyrax-text tracking-tight tabular-nums">
              {value.toLocaleString("es-CL")}
            </p>
            <div
              className={`mt-3 flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald-600 dark:text-lyrax-primary" : "text-red-500"
                }`}>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="card lg:col-span-3 animate-fade-up stagger-2">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-base font-semibold text-lyrax-text">Bienvenido</h3>
              <p className="text-sm text-lyrax-text-secondary mt-0.5">
                Centro de control multi-tenant
              </p>
            </div>
            <span className="status-pill">Operativo</span>
          </div>
          <p className="text-lyrax-text-secondary leading-relaxed text-[15px]">
            Gestiona workspaces, planes y catálogos de homologación desde un solo panel.
            El acento mint guía acciones críticas; el navy ancla la navegación.
          </p>
          <div className="mt-6 h-28 rounded-lyrax bg-gradient-to-br from-lyrax-primary/15 via-lyrax-primary/5 to-transparent relative overflow-hidden">
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 400 112"
              preserveAspectRatio="none"
              aria-hidden
            >
              <defs>
                <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#229E64" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#229E64" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0 80 C40 70 60 40 100 45 C140 50 160 85 200 70 C240 55 260 25 300 35 C340 45 360 55 400 30 L400 112 L0 112 Z"
                fill="url(#areaFill)"
              />
              <path
                d="M0 80 C40 70 60 40 100 45 C140 50 160 85 200 70 C240 55 260 25 300 35 C340 45 360 55 400 30"
                fill="none"
                stroke="#229E64"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>

        <div className="card lg:col-span-2 animate-fade-up stagger-3">
          <h3 className="text-base font-semibold text-lyrax-text mb-1">Acciones rápidas</h3>
          <p className="text-sm text-lyrax-text-secondary mb-5">Atajos del panel</p>
          <div className="space-y-2">
            <Link
              to="/tenants"
              className="flex items-center justify-between p-3 rounded-lyrax bg-lyrax-bg hover:bg-lyrax-primary/10 transition-colors group"
            >
              <div>
                <p className="font-medium text-lyrax-text text-sm">Ver tenants</p>
                <p className="text-xs text-lyrax-text-secondary">Workspaces existentes</p>
              </div>
              <ArrowUpRight className="w-4 h-4 text-lyrax-text-secondary group-hover:text-lyrax-primary transition-colors" />
            </Link>
            <Link
              to="/plans"
              className="flex items-center justify-between p-3 rounded-lyrax bg-lyrax-bg hover:bg-lyrax-primary/10 transition-colors group"
            >
              <div>
                <p className="font-medium text-lyrax-text text-sm">Gestionar planes</p>
                <p className="text-xs text-lyrax-text-secondary">Suscripciones</p>
              </div>
              <ArrowUpRight className="w-4 h-4 text-lyrax-text-secondary group-hover:text-lyrax-primary transition-colors" />
            </Link>
            <Link
              to="/mantenedores/estados"
              className="flex items-center justify-between p-3 rounded-lyrax bg-lyrax-bg hover:bg-lyrax-primary/10 transition-colors group"
            >
              <div>
                <p className="font-medium text-lyrax-text text-sm">Estados homologados</p>
                <p className="text-xs text-lyrax-text-secondary">Catálogo global</p>
              </div>
              <ArrowUpRight className="w-4 h-4 text-lyrax-text-secondary group-hover:text-lyrax-primary transition-colors" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
